import { Button, TextButton } from '@creditornot/cb-button';
import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { InputAlert } from '@creditornot/cb-input';
import { Spinner } from '@creditornot/cb-progress';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { Tag } from '@creditornot/cb-tag';
import { ModalFooter, ModalHeader } from '@creditornot/cbt-modal';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import React, { useContext, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { APIErrorNotification, WoltModal } from 'common/components';
import { WideAreaCheckbox } from 'common/components';
import ErrorToastContent from 'common/components/ErrorToastContent';
import { Typography as T } from 'common/typography';
import { isDefined } from 'common/utils';
import { MenuMetaContext } from 'modules/menu/MenuMetaContext';
import useBulkSelection from 'modules/menu/bulk-actions/useBulkSelection';

import { useMenuSyncContext } from './MenuSyncContext';
import { fetchMenuPushes, revokePendingPushes } from './api';
import { isIntentScheduled } from './useMenuSyncStatus';
import { reportMenuSyncActionInitiate } from '../behavioral';

type Props = {
  onClose: () => void;
};

const Root = styled(WoltModal)`
  width: 640px;
  border-radius: 8px;
  background-color: ${cbSemanticCssVars.surfaceMain};
  display: flex;
  flex-direction: column;
`;

const VenueList = styled.div`
  background: ${cbSemanticCssVars.surfaceMain};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  max-height: 60vh;
  flex: 1 1 auto;
  overflow-y: auto;
`;

const InfoContainer = styled.div`
  padding: 16px 24px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const Toolbar = styled.div`
  display: flex;
  width: 100%;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  height: 68px;
  padding: 16px 24px;
`;

const LoadingContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
`;

const VenueItem = styled.div<{ selected: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 8px 24px 8px 0;
  flex-shrink: 0;
  background-color: ${({ selected }) => (selected ? colors.wolt4 : cbSemanticCssVars.surfaceMain)};
  transition: background-color 100ms;
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;
  & + & {
    border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  }
  &:hover {
    background-color: ${({ selected }) =>
      selected ? colors.wolt8 : cssThemedValue({ light: colors.pepper8, dark: colors.salt4 })};
  }
`;

const NameAndTagContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const VenueName = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const CancelPushesModal = ({ onClose }: Props) => {
  const { menuId } = useContext(MenuMetaContext);
  const { refetchSyncInfo } = useMenuSyncContext();

  const [nextPageToken, setNextPageToken] = useState<string | null>(null);

  const {
    data: pendingMenuPushes,
    isLoading: loadingPendingMenuPushes,
    error: loadingError,
  } = useQuery(
    ['pending-menu-pushes', menuId],
    /*
      Temporary page size of 500, so that 99.99% of the time we don't need pagination
      TODO: add infinite list pagination & fetch everything when clicking the "Select all" button 
    */
    async () => fetchMenuPushes(menuId, nextPageToken, ['pending'], 500),
    {
      onSuccess: data => {
        setNextPageToken(data.next_push_page_token);
      },
      retry: false,
    },
  );

  const {
    selectedIds: selectedIntentIds,
    select: selectIntent,
    deselect: deselectIntent,
    selectMultiple,
    clearSelection,
  } = useBulkSelection();

  const handleSelectAll = () => {
    if (!pendingMenuPushes) {
      return;
    }
    const allMenuIds = pendingMenuPushes?.push_intents.map(intent => intent.id || undefined).filter(isDefined);

    selectMultiple(allMenuIds);
  };

  const { mutate: handleSubmit, isLoading: submitting } = useMutation(
    () => revokePendingPushes(menuId, [...selectedIntentIds]),
    {
      onMutate: () => {
        reportMenuSyncActionInitiate(
          'cancel',
          selectedIntentIds.size,
          pendingMenuPushes ? pendingMenuPushes.push_intents.length : -1,
        );
      },
      onSuccess: async () => {
        await refetchSyncInfo();
        toast.success(<FormattedMessage id="menu.menu-sync.cancel-pushes.success-description" />);
      },
      onError: (error: any) => {
        toast.error(<ErrorToastContent error={error} />, { duration: Infinity });
      },
      onSettled: () => {
        onClose();
      },
    },
  );

  const allSelected = !!pendingMenuPushes
    ? pendingMenuPushes.push_intents.every(intent => selectedIntentIds.has(intent.id))
    : false;

  return (
    <Root isOpen onRequestClose={onClose}>
      <ModalHeader onCloseClick={onClose}>
        <FormattedMessage id="menu.menu-sync.cancel-pushes.header" />
      </ModalHeader>
      <InfoContainer>
        <InputAlert variant="info">
          <FormattedMessage id="menu.menu-sync.cancel-pushes.info" />
        </InputAlert>
      </InfoContainer>
      {pendingMenuPushes && (
        <Toolbar>
          {!allSelected && (
            <TextButton variant="brand" onClick={handleSelectAll} style={{ marginInlineEnd: '16px' }}>
              <FormattedMessage id="menu.menu-sync.select-all-venues" />
            </TextButton>
          )}
          {selectedIntentIds.size > 0 && (
            <TextButton variant="negative" onClick={clearSelection}>
              <FormattedMessage
                id="menu.menu-sync.deselect-venues-with-count"
                values={{ count: selectedIntentIds.size }}
              />
            </TextButton>
          )}
        </Toolbar>
      )}
      <VenueList>
        {loadingPendingMenuPushes && (
          <LoadingContainer>
            <Spinner size="large" />
          </LoadingContainer>
        )}
        {!!loadingError && (
          <LoadingContainer>
            <APIErrorNotification error={loadingError} style={{ width: '100%' }} />
          </LoadingContainer>
        )}
        {!!pendingMenuPushes &&
          pendingMenuPushes.push_intents.length > 0 &&
          pendingMenuPushes.push_intents.map(intent => (
            <VenueItem
              key={intent.id}
              selected={selectedIntentIds.has(intent.id)}
              onClick={() => {
                if (!intent.id) {
                  return;
                }
                if (selectedIntentIds.has(intent.id)) {
                  deselectIntent(intent.id);
                } else {
                  selectIntent(intent.id);
                }
              }}
            >
              <WideAreaCheckbox
                disabled={!intent.id}
                onToggle={() => {
                  if (!intent.id) {
                    return;
                  }
                  if (selectedIntentIds.has(intent.id)) {
                    deselectIntent(intent.id);
                  } else {
                    selectIntent(intent.id);
                  }
                }}
                selectionStatus={intent.id && selectedIntentIds.has(intent.id) ? 'selected' : 'not-selected'}
              />
              <NameAndTagContainer>
                <VenueName>
                  <T.Body3>{intent.venue_name[0].value}</T.Body3>
                </VenueName>
                {isIntentScheduled(intent.scheduled_at) ? (
                  <Tag variant="lightBlue" uppercase size="tiny">
                    <FormattedMessage id="menu.menu-sync.state.scheduled" />
                    {` - ${DateTime.fromISO(intent.scheduled_at).toLocaleString(DateTime.DATETIME_SHORT)}`}
                  </Tag>
                ) : (
                  <Tag variant="lightBlack" uppercase size="tiny">
                    <FormattedMessage id="menu.menu-sync.state.pending" />
                  </Tag>
                )}
              </NameAndTagContainer>
            </VenueItem>
          ))}
      </VenueList>
      <StyledModalFooter>
        <Button size="small" onClick={onClose} variant="secondary" style={{ marginInlineEnd: '16px' }}>
          <FormattedMessage id="state.cancel" />
        </Button>
        <Button
          disabled={selectedIntentIds.size === 0 || submitting}
          loading={submitting}
          onClick={() => handleSubmit()}
          size="small"
          variant="primary"
        >
          <FormattedMessage id="menu.menu-sync.cancel-pushes.submit" />
        </Button>
      </StyledModalFooter>
    </Root>
  );
};

export default CancelPushesModal;
